<template>
  <CCard v-if="suportado">
    <CCardHeader class="eventos__cabecalho">
      Eventos recebidos
      <CButton
        size="sm"
        variant="outline"
        color="secondary"
        :disabled="carregando"
        @click="carregar"
      >
        <fa-icon icon="sync" /> {{ carregando ? 'Atualizando...' : 'Atualizar' }}
      </CButton>
    </CCardHeader>

    <CCardBody>
      <p class="eventos__intro">
        O que o serviço mandou para cá, como chegou. Serve para conferir se o
        envio está configurado do outro lado e para ver de qual campo veio cada
        dado antes de montar o mapeamento.
      </p>

      <div v-if="erro" class="eventos__vazio">{{ erro }}</div>

      <div v-else-if="!eventos.length && !carregando" class="eventos__vazio">
        Nada recebido ainda. Assim que o primeiro envio chegar, ele aparece aqui.
      </div>

      <ul v-else class="eventos__lista">
        <li
          v-for="evento in eventos"
          :key="evento.id"
          class="evento"
          :class="{ 'evento--erro': !Number(evento.sucesso) }"
        >
          <button
            type="button"
            class="evento__linha"
            :aria-expanded="aberto === evento.id ? 'true' : 'false'"
            @click="alternar(evento.id)"
          >
            <span class="evento__marca" :class="Number(evento.sucesso) ? 'evento__marca--ok' : 'evento__marca--erro'"></span>
            <span class="evento__data">{{ dataHora(evento.datahora_criacao) }}</span>
            <span class="evento__identificador">{{ evento.tipo_id || '—' }}</span>
            <span class="evento__mensagem">{{ evento.mensagem || '' }}</span>
            <fa-icon :icon="aberto === evento.id ? 'chevron-up' : 'chevron-down'" />
          </button>

          <div v-if="aberto === evento.id" class="evento__payload">
            <div class="evento__payload-titulo">
              Recebido
              <!--
                O botão só existe onde copiar funciona: a API de área de
                transferência é indefinida fora de origem segura, caso real em
                instalação por HTTP, e ali o clique não fazia nada nem avisava.
                O JSON abaixo continua selecionável à mão.
              -->
              <button
                v-if="podeCopiar"
                type="button"
                class="evento__copiar"
                @click="copiar(evento)"
              >
                {{ copiado === evento.id ? 'Copiado!' : 'Copiar' }}
              </button>
            </div>
            <pre class="evento__json"><span
              v-for="(token, indice) in tokens(evento.dados_enviados)"
              :key="indice"
              :class="token.classe"
            >{{ token.texto }}</span></pre>

            <template v-if="temResposta(evento)">
              <div class="evento__payload-titulo">Respondido</div>
              <pre class="evento__json"><span
                v-for="(token, indice) in tokens(evento.dados_recebidos)"
                :key="'r' + indice"
                :class="token.classe"
              >{{ token.texto }}</span></pre>
            </template>
          </div>
        </li>
      </ul>

      <router-link v-if="eventos.length" class="eventos__todos" :to="linkHistorico">
        Ver o histórico completo
      </router-link>
    </CCardBody>
  </CCard>
</template>

<script>
/*
 * Últimos eventos que o serviço externo mandou para uma integração.
 *
 * O log já existia e é completo — tem o JSON, o resultado e a mensagem —, mas
 * morava no módulo de Logs, sob um tipo cujo nome quem configura a integração
 * não tem por que saber. Aqui ele aparece ao lado da configuração que o produz,
 * que é onde a pergunta "chegou alguma coisa?" nasce.
 *
 * O bloco só existe quando o conector declara `log.tipo` no manifesto; sem
 * isso, não há o que listar e a tela não mostra uma caixa vazia.
 */
import { get } from '@/helpers/apiRequest';
import { tokensJson, formataJson } from '@/helpers/jsonTokens';

export default {
  name: 'EventosRecebidos',
  props: {
    integracaoId: { type: [Number, String], required: true },
    tipoLog: { type: String, default: '' },
  },
  data() {
    return {
      eventos: [],
      aberto: null,
      copiado: null,
      carregando: false,
      erro: '',
    };
  },
  computed: {
    podeCopiar() {
      return !!(navigator.clipboard && navigator.clipboard.writeText);
    },

    suportado() {
      return !!this.tipoLog && !!this.integracaoId;
    },

    linkHistorico() {
      return {
        path: '/configuracoes/logs',
        query: { aba: 'integracao', tipos: this.tipoLog },
      };
    },
  },
  mounted() {
    if (this.suportado) {
      this.carregar();
    }
  },
  methods: {
    carregar() {
      this.carregando = true;
      this.erro = '';

      get(`/admin/integracoes/${this.integracaoId}/eventos?limite=10`)
        .then((resposta) => {
          this.eventos = (resposta && resposta.data) || [];
        })
        .catch(() => {
          this.eventos = [];
          this.erro = 'Não foi possível carregar os eventos recebidos.';
        })
        .finally(() => {
          this.carregando = false;
        });
    },

    alternar(id) {
      this.aberto = this.aberto === id ? null : id;
    },

    tokens(bruto) {
      return tokensJson(formataJson(bruto) || '— sem conteúdo —');
    },

    /*
     * Retorno vazio é o normal em recebimento por webhook; mostrar um bloco
     * "Respondido" com `null` só ocuparia espaço.
     */
    temResposta(evento) {
      const bruto = evento.dados_recebidos;

      return !!bruto && bruto !== 'null' && bruto !== '[]' && bruto !== '{}';
    },

    copiar(evento) {
      const texto = formataJson(evento.dados_enviados);

      if (!this.podeCopiar) {
        return;
      }

      navigator.clipboard.writeText(texto)
        .then(() => {
          this.copiado = evento.id;
          setTimeout(() => {
            this.copiado = null;
          }, 2000);
        })
        .catch(() => {
          this.$swal({
            title: 'Não foi possível copiar',
            text: 'O navegador recusou o acesso à área de transferência. '
              + 'Selecione o conteúdo e copie à mão.',
            icon: 'error',
          });
        });
    },

    dataHora(valor) {
      if (!valor) {
        return '—';
      }

      const data = new Date(String(valor).replace(' ', 'T'));

      return Number.isNaN(data.getTime())
        ? String(valor)
        : data.toLocaleString('pt-BR');
    },
  },
};
</script>

<style scoped>
.eventos__cabecalho {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.eventos__intro {
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 12px;
}

.eventos__vazio {
  color: #6b7280;
  font-size: 13px;
  padding: 12px;
  background: #f8f9fb;
  border-radius: 8px;
}

.eventos__lista {
  list-style: none;
  margin: 0;
  padding: 0;
}

.evento {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
}

.evento--erro {
  border-color: #f3c8c8;
}

.evento__linha {
  align-items: center;
  background: #fff;
  border: 0;
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  text-align: left;
  width: 100%;
}

.evento__linha:hover {
  background: #f8f9fb;
}

.evento__marca {
  border-radius: 50%;
  flex: 0 0 8px;
  height: 8px;
  width: 8px;
}

.evento__marca--ok {
  background: #2298a3;
}

.evento__marca--erro {
  background: #d9534f;
}

.evento__data {
  color: #374151;
  font-size: 12px;
  white-space: nowrap;
}

.evento__identificador {
  color: #3a3e81;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.evento__mensagem {
  color: #6b7280;
  flex: 1;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.evento__payload {
  background: #fbfbfd;
  border-top: 1px solid #e4e7eb;
  padding: 10px 12px;
}

.evento__payload-titulo {
  align-items: center;
  color: #6b7280;
  display: flex;
  font-size: 11px;
  font-weight: 700;
  gap: 8px;
  justify-content: space-between;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.evento__copiar {
  background: none;
  border: 0;
  color: #3a3e81;
  font-size: 11px;
  padding: 0;
}

.evento__json {
  background: #fff;
  border: 1px solid #eceaf3;
  border-radius: 6px;
  font-size: 11.5px;
  margin: 0 0 10px;
  max-height: 280px;
  overflow: auto;
  padding: 10px;
  white-space: pre-wrap;
  word-break: break-word;
}

.evento__json .j-key {
  color: #3a3e81;
}

.evento__json .j-str {
  color: #0f766e;
}

.evento__json .j-bool {
  color: #b45309;
}

.evento__json .j-num {
  color: #9333ea;
}

.evento__json .j-punc {
  color: #6b7280;
}

.eventos__todos {
  display: inline-block;
  font-size: 12px;
  margin-top: 4px;
}
</style>
